import { Box, Typography } from '@mui/material';
import type {
  StructuredContent,
  StructuredContentDocument,
} from '@repo/supergraph-schemas/structuredContent/types';
import { Faq } from '../../../Faq';
import { StructuredContentRenderer } from '../..';

type FaqItem = {
  question?: string;
  answer?: StructuredContent;
};

type FAQNodeProps = {
  postId?: number;
  templateOverride?: unknown;
  faqs?: FaqItem[];
  canonicalUrl?: string;
  defaultExpanded?: boolean;
};

type Props = StructuredContentDocument<FAQNodeProps>;

function isCompleteFaq(
  item: FaqItem,
): item is FaqItem & { question: string; answer: StructuredContent } {
  return Boolean(item.question && item.answer);
}

export function FAQ({ props }: Props) {
  const { faqs, canonicalUrl = '', defaultExpanded = false } = props;

  const completeFaqs = (faqs ?? []).filter(isCompleteFaq);

  if (completeFaqs.length === 0) {
    return null;
  }

  return (
    <Box
      component="section"
      data-testid="faqs-section"
      aria-labelledby="faq"
      sx={{
        '& summary:hover': {
          outline: 'none',
          bgcolor: 'neutral.lightest',
        },
        '& [data-testid="accordion-details"]': {
          paddingX: '0.850rem',
          paddingTop: 0,
        },
      }}
    >
      <Box sx={{ marginBottom: '1.5rem' }}>
        {/* Used span instead of h2 to match the live version of the FAQ section */}
        <Typography
          id="faq"
          component="span"
          variant="headline"
          sx={{ fontSize: '1.5rem', fontWeight: 500, lineHeight: 4 / 3 }}
        >
          Frequently asked questions
        </Typography>
      </Box>
      <Faq
        faqs={completeFaqs.map(faq => ({
          question: (
            <Typography
              variant="text"
              sx={{
                display: 'block',
                'details[open] &': {
                  fontWeight: 500,
                },
                padding: 1,
              }}
            >
              {faq.question}
            </Typography>
          ),
          answer: <StructuredContentRenderer doc={faq.answer} />,
        }))}
        canonicalUrl={canonicalUrl}
        defaultExpanded={defaultExpanded}
      />
    </Box>
  );
}
